import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { triggerPoll } from '../api';

const THRESHOLD = 70;
const MAX_PULL = 130;

export default function PullToRefresh() {
  const qc = useQueryClient();
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);

  useEffect(() => {
    function onStart(e: TouchEvent) {
      if (window.scrollY > 0) return;
      if (e.touches.length !== 1) return;
      startY.current = e.touches[0].clientY;
      active.current = true;
    }
    function onMove(e: TouchEvent) {
      if (!active.current || startY.current == null) return;
      if (window.scrollY > 0) {
        active.current = false;
        setDistance(0);
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setDistance(0); return; }
      e.preventDefault();
      // Rubber-band: ease-out as we approach MAX_PULL.
      const eased = Math.min(MAX_PULL, dy * 0.55);
      setDistance(eased);
    }
    async function onEnd() {
      if (!active.current) return;
      active.current = false;
      if (distance >= THRESHOLD && !refreshing) {
        setRefreshing(true);
        setDistance(THRESHOLD);
        try {
          await triggerPoll();
          await qc.invalidateQueries();
        } finally {
          setTimeout(() => { setRefreshing(false); setDistance(0); }, 350);
        }
      } else {
        setDistance(0);
      }
      startY.current = null;
    }
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart as any);
      window.removeEventListener('touchmove', onMove as any);
      window.removeEventListener('touchend', onEnd as any);
      window.removeEventListener('touchcancel', onEnd as any);
    };
  }, [distance, refreshing, qc]);

  const progress = Math.min(1, distance / THRESHOLD);

  if (distance < 4 && !refreshing) return null;

  return (
    <div
      className="lg:hidden fixed top-0 inset-x-0 z-[55] flex justify-center pointer-events-none"
      style={{ paddingTop: `calc(env(safe-area-inset-top) + ${Math.max(8, distance - 20)}px)` }}
    >
      <div
        className={`flex items-center justify-center w-9 h-9 rounded-full bg-ink-800 border border-ink-700 shadow-glow transition-transform`}
        style={{ transform: `rotate(${progress * 360}deg)` }}
      >
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`text-pitch-400 ${refreshing ? 'animate-spin' : ''}`}
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </div>
    </div>
  );
}
